import { create } from 'zustand';
import {
  cancelEvent,
  createAssignment,
  createEvent,
  createEventType,
  createUnavailability,
  deleteAssignment,
  deleteEventType,
  deleteUnavailability,
  generateEventsFromType,
  listAssignments,
  listEventTypes,
  listEvents,
  listMyAssignments,
  listUnavailabilities,
  updateAssignmentStatus,
  updateEvent,
  updateEventType,
  updateUnavailability,
  type EventFilters,
} from '../api/events';
import type {
  Event,
  EventAssignment,
  EventType,
  MemberUnavailability,
} from '../types/event';

interface EventStore {
  // State
  eventTypes: EventType[];
  events: Event[];
  assignments: Record<string, EventAssignment[]>; // eventId -> assignments
  myAssignments: EventAssignment[];
  unavailabilities: MemberUnavailability[];
  isLoading: boolean;
  error: string | null;

  // EventType actions
  loadEventTypes: (teamId: string) => Promise<void>;
  addEventType: (teamId: string, payload: Parameters<typeof createEventType>[1]) => Promise<EventType>;
  editEventType: (teamId: string, typeId: string, payload: Parameters<typeof updateEventType>[2]) => Promise<void>;
  removeEventType: (teamId: string, typeId: string) => Promise<void>;
  generateEvents: (teamId: string, typeId: string, startDate: string, endDate: string) => Promise<Event[]>;

  // Event actions
  loadEvents: (teamId: string, filters?: EventFilters) => Promise<void>;
  addEvent: (teamId: string, payload: Parameters<typeof createEvent>[1]) => Promise<Event>;
  editEvent: (teamId: string, eventId: string, payload: Parameters<typeof updateEvent>[2]) => Promise<void>;
  removeEvent: (teamId: string, eventId: string) => Promise<void>;

  // Assignment actions
  loadAssignments: (teamId: string, eventId: string) => Promise<void>;
  loadMyAssignments: (teamId: string, dateFrom?: string, dateTo?: string) => Promise<void>;
  addAssignment: (teamId: string, eventId: string, memberId: string, instrumentId: string) => Promise<void>;
  respondAssignment: (teamId: string, eventId: string, assignmentId: string, status: 'CONFIRMED' | 'DECLINED', reason?: string) => Promise<void>;
  removeAssignment: (teamId: string, eventId: string, assignmentId: string) => Promise<void>;

  // Unavailability actions
  loadUnavailabilities: (teamId: string) => Promise<void>;
  addUnavailability: (teamId: string, startDate: string, endDate: string, reason?: string) => Promise<void>;
  editUnavailability: (teamId: string, unavailId: string, payload: Parameters<typeof updateUnavailability>[2]) => Promise<void>;
  removeUnavailability: (teamId: string, unavailId: string) => Promise<void>;

  clearError: () => void;
}

export const useEventStore = create<EventStore>((set, get) => ({
  eventTypes: [],
  events: [],
  assignments: {},
  myAssignments: [],
  unavailabilities: [],
  isLoading: false,
  error: null,

  // ----- EventType -----

  loadEventTypes: async (teamId) => {
    set({ isLoading: true, error: null });
    try {
      const eventTypes = await listEventTypes(teamId);
      set({ eventTypes });
    } catch {
      set({ error: 'Failed to load event types' });
    } finally {
      set({ isLoading: false });
    }
  },

  addEventType: async (teamId, payload) => {
    const et = await createEventType(teamId, payload);
    set((s) => ({ eventTypes: [...s.eventTypes, et] }));
    return et;
  },

  editEventType: async (teamId, typeId, payload) => {
    const et = await updateEventType(teamId, typeId, payload);
    set((s) => ({ eventTypes: s.eventTypes.map((t) => (t.id === typeId ? et : t)) }));
  },

  removeEventType: async (teamId, typeId) => {
    await deleteEventType(teamId, typeId);
    set((s) => ({ eventTypes: s.eventTypes.filter((t) => t.id !== typeId) }));
  },

  generateEvents: async (teamId, typeId, startDate, endDate) => {
    const result = await generateEventsFromType(teamId, typeId, startDate, endDate);
    set((s) => ({ events: [...s.events, ...result.events] }));
    return result.events;
  },

  // ----- Event -----

  loadEvents: async (teamId, filters) => {
    set({ isLoading: true, error: null });
    try {
      const events = await listEvents(teamId, filters);
      set({ events });
    } catch {
      set({ error: 'Failed to load events' });
    } finally {
      set({ isLoading: false });
    }
  },

  addEvent: async (teamId, payload) => {
    const event = await createEvent(teamId, payload);
    set((s) => ({ events: [...s.events, event] }));
    return event;
  },

  editEvent: async (teamId, eventId, payload) => {
    const event = await updateEvent(teamId, eventId, payload);
    set((s) => ({ events: s.events.map((e) => (e.id === eventId ? event : e)) }));
  },

  removeEvent: async (teamId, eventId) => {
    await cancelEvent(teamId, eventId);
    set((s) => ({
      events: s.events.map((e) => (e.id === eventId ? { ...e, status: 'CANCELLED' as const } : e)),
    }));
  },

  // ----- Assignment -----

  loadAssignments: async (teamId, eventId) => {
    const items = await listAssignments(teamId, eventId);
    set((s) => ({ assignments: { ...s.assignments, [eventId]: items } }));
  },

  loadMyAssignments: async (teamId, dateFrom, dateTo) => {
    set({ isLoading: true, error: null });
    try {
      const myAssignments = await listMyAssignments(teamId, dateFrom, dateTo);
      set({ myAssignments });
    } catch {
      set({ error: 'Failed to load assignments' });
    } finally {
      set({ isLoading: false });
    }
  },

  addAssignment: async (teamId, eventId, memberId, instrumentId) => {
    const item = await createAssignment(teamId, eventId, memberId, instrumentId);
    set((s) => ({
      assignments: {
        ...s.assignments,
        [eventId]: [...(s.assignments[eventId] ?? []), item],
      },
    }));
  },

  respondAssignment: async (teamId, eventId, assignmentId, status, reason) => {
    const updated = await updateAssignmentStatus(teamId, eventId, assignmentId, status, reason);
    set((s) => ({
      myAssignments: s.myAssignments.map((a) => (a.id === assignmentId ? updated : a)),
      assignments: {
        ...s.assignments,
        [eventId]: (s.assignments[eventId] ?? []).map((a) => (a.id === assignmentId ? updated : a)),
      },
    }));
  },

  removeAssignment: async (teamId, eventId, assignmentId) => {
    await deleteAssignment(teamId, eventId, assignmentId);
    set((s) => ({
      assignments: {
        ...s.assignments,
        [eventId]: (s.assignments[eventId] ?? []).filter((a) => a.id !== assignmentId),
      },
    }));
  },

  // ----- Unavailability -----

  loadUnavailabilities: async (teamId) => {
    set({ isLoading: true, error: null });
    try {
      const unavailabilities = await listUnavailabilities(teamId);
      set({ unavailabilities });
    } catch {
      set({ error: 'Failed to load unavailabilities' });
    } finally {
      set({ isLoading: false });
    }
  },

  addUnavailability: async (teamId, startDate, endDate, reason) => {
    const item = await createUnavailability(teamId, startDate, endDate, reason);
    set((s) => ({ unavailabilities: [...s.unavailabilities, item] }));
  },

  editUnavailability: async (teamId, unavailId, payload) => {
    const item = await updateUnavailability(teamId, unavailId, payload);
    set((s) => ({
      unavailabilities: s.unavailabilities.map((u) => (u.id === unavailId ? item : u)),
    }));
  },

  removeUnavailability: async (teamId, unavailId) => {
    await deleteUnavailability(teamId, unavailId);
    set((s) => ({ unavailabilities: s.unavailabilities.filter((u) => u.id !== unavailId) }));
  },

  clearError: () => set({ error: null }),
}));
